/**
 * Detail-page modes.
 *
 * A detail page (Activity, Inventory, ChemicalProduct, ...) is always rendered
 * in exactly one mode, and the mode is embedded in the URL — see `routes.tsx`,
 * e.g. `/:mode(${Mode.VIEW}|${Mode.EDIT}|${Mode.CLONE}|${Mode.UNDO_DELETE})`.
 * That means the string *values* below are load-bearing: they must stay
 * exactly `'create'`, `'view'`, etc. or existing URLs break.
 *
 * All 7 members flow through the same `mode` variable/param across the app,
 * so they belong in one type — omitting any means that `mode` value can't be
 * typed as `Mode`.
 *
 * `as const` keeps each value at its literal type; the `keyof typeof` line
 * derives the union `'create' | 'view' | ...` from the object, so adding a
 * member here updates the type automatically. `const Mode` and `type Mode`
 * share a name intentionally (declaration merging) — call sites use
 * `Mode.VIEW` for the value and `mode: Mode` for the type.
 */
export const Mode = {
  CREATE: 'create',
  VIEW: 'view',
  EDIT: 'edit',
  REPORT: 'report',
  CLONE: 'clone',
  UNDO_DELETE: 'undoDelete',
  BATCH_UPDATE: 'batchUpdate',
} as const;

export type Mode = (typeof Mode)[keyof typeof Mode];

export const MOBILE = 'mobile';
export const SHARE_DATA_VIEW = '?sharedDataView=';

export const CLONE_PREFIX = 'Clone of ';

export const CONTACT_BATCH_MODE = Object.freeze({
  ADD: 'ADD',
  OVERRIDE: 'OVERRIDE',
  REMOVE: 'REMOVE',
  REPLACE: 'REPLACE',
});
export const HISTORY_OVERVIEW_TYPE = 'history';
export const ENTITY_OVERVIEW_TYPE = 'entity';
export const DELETED_OVERVIEW_TYPE = 'deleted';
export const GEN_ORG_UNIT = 'GEN_ORG_UNIT';

export const HIGH_VOLATILITY = 100;
export const MEDIUM_VOLATILITY = 99;
export const LOW_VOLATILITY = 98;

export const LARGE_AMOUNT = 92;
export const MEDIUM_AMOUNT = 91;
export const SMALL_AMOUNT = 90;

export const HAZARD_TYPE_ENV_HAZARDS = 'hazard.type.environmental.hazard';

export const RISK_TYPE_PRESENCE_TYPE_CODE = 'polichem.risk.type.presence.type';
export const RISK_TYPE_WATERCOUSE_CODE = 'polichem.risk.type.watercourse';
export const RISK_TYPE_CONTAINER_INSPECTION_CODE = 'polichem.risk.type.container.inspection';
export const RISK_TYPE_PIPING_INSPECTION_CODE = 'polichem.risk.type.piping.inspection';
export const RISK_TYPE_PROC_LOAD_UNLOAD_CODE = 'polichem.risk.type.proc.load.unload';
export const RISK_TYPE_ADEQUACY_RETEN_CODE = 'polichem.risk.type.adequacy.reten';
export const RISK_TYPE_TEST_RETEN_TIGHTNESS_CODE = 'polichem.risk.type.test.reten.tightness';
export const RISK_TYPE_LEAK_DETECT_INSPECTION_CODE = 'polichem.risk.type.leak.detect.inspection';
export const RISK_TYPE_OVERFLOW_DETECT_INSPECTION_CODE =
  'polichem.risk.type.overflow.detect.inspection';
export const RISK_TYPE_ADEQUACY_SEPAR_BUF_TANK_CODE = 'polichem.risk.type.adequacy.separ.buf.tank';
export const RISK_TYPE_SEPAR_BUF_TANK_INSPECTION_CODE =
  'polichem.risk.type.separ.buf.tank.inspection';
export const RISK_TYPE_SPILL_KIT_CODE = 'polichem.risk.type.spill.kit';
export const RISK_TYPE_LIQUID_CATEGORY_CODE = 'polichem.risk.type.liquid.category';

export const RISK_TYPE_VOLATILITY_CODE = 'chemical.risk.type.volatility';
export const RISK_TYPE_DUST_TENDENCY_CODE = 'chemical.risk.type.dust.tendency';
export const RISK_TYPE_SUBSTANCE_AMOUNT_CODE = 'chemical.risk.type.substance.amount';
export const RISK_TYPE_HAZARD_BAND_CODE = 'chemical.risk.type.hazard.band';

export const filterFieldDataType = {
  text: 'TEXT',
  numeric: 'NUMERIC',
  multiple_choice: 'MULTIPLE_CHOICE',
  multiple_auto_complete: 'MULTIPLE_AUTO_COMPLETE',
  multiple_auto_complete_contains: 'MULTIPLE_AUTO_COMPLETE_CONTAINS',
  multiple_auto_complete_or_text_contains: 'MULTIPLE_AUTO_COMPLETE_OR_TEXT_CONTAINS',
  date: 'DATE',
  boolean: 'BOOLEAN',
  boolean_dropdown: 'BOOLEAN_DROPDOWN',
  dropdown: 'DROPDOWN',
};

export const filterFieldSuffix = {
  more_than: '_MORE_THAN',
  less_than: '_LESS_THAN',
};

export const separators = {
  hazards: ', ',
};

export const FILTER_EMPTY_PLACEHOLDER = '<EMPTY>';
export const FILTER_NOT_EMPTY_PLACEHOLDER = '<NOT EMPTY>';
export const FILTER_EMPTY_BOOLEAN_PLACEHOLDER = 'EMPTY_VALUE';

export const entityType = {
  inventory: 'inventory',
  product: 'product',
  productHist: 'productHist',
  inventoryHist: 'inventoryHist',
  inventoryAllForExport: 'inventoryAllForExport',
  building: 'building',
  batchUpdate: 'batchUpdate',
  batchUpdateSelected: 'batchUpdateSelected',
  activity: 'activity',
};

export const defaultFilterColumn = {
  GEN_TRADE_CHEMICAL_NAMES: 'GEN_TRADE_CHEMICAL_NAMES',
  GEN_CONTACTS: 'GEN_CONTACTS',
  GEN_ORG_UNIT: 'GEN_ORG_UNIT',
  GEN_LOCATION_NAME: 'GEN_LOCATION_NAME',
  HIST_IS_DELETED: 'HIST_IS_DELETED',
  GEN_IS_CHECK_VALID: 'GEN_IS_CHECK_VALID',
  GEN_HAS_PRECISE_LOCATION: 'GEN_HAS_PRECISE_LOCATION',
};

export const userDefaultFilterPropHiddenValue = {
  HIST_IS_DELETED: 'YES_VALUE',
};

export const dbFieldType = {
  true: 'YES_VALUE',
  false: 'NO_VALUE',
  none: 'EMPTY_VALUE',
};

export const recordOptionType = {
  edit: 'edit',
  view: 'view',
  clone: 'clone',
  history: 'history',
  delete: 'delete',
  undoDelete: 'undoDelete',
};

export const recordOptionHighlightColor = {
  delete: '#ffe6e6',
  clone: '#fffaf3',
};

export const translationSuffixes = {
  placeholder: '.placeholder',
  label: '.label',
  tooltip: '.tooltip',
  unit: '.unit',
};

export const inforEamUrlSuffix = {
  ENV_ASSET: 'asset.php?code=',
  ENV_FUNCTIONAL_POSITION: 'position.php?code=',
};

export const HAZARD_ICONS_PATH = '/images/hazardIcons/';
export const PPE_ICONS_PATH = '/images/ppeIcons/';
export const LOGO_IMAGE_PATH = '/images/logo/ceres_graphic_with_back_256_black.png';
export const FILE_EXTENSION_PNG_IMAGE = '.png';
export const FILES_TOTAL_SIZE_LIMIT = 140000000; //140mb, due to chrome 'aw snap' page + max heap size on backend

export const fieldKey = {
  GEN_LOCATION_NAME: 'GEN_LOCATION_NAME',
  GEN_EXPERIMENT: 'GEN_EXPERIMENT',
  GEN_CONTACTS: 'GEN_CONTACTS',
  ENV_FUNCTIONAL_POSITION: 'ENV_FUNCTIONAL_POSITION',
  ENV_ASSET: 'ENV_ASSET',
  GEN_TRADE_CHEMICAL_NAMES: 'GEN_TRADE_CHEMICAL_NAMES',
  GEN_TRADE_NAME: 'GEN_TRADE_NAME',
  GEN_CHEMICAL_NAME: 'GEN_CHEMICAL_NAME',
  GEN_QUANTITY: 'GEN_QUANTITY',
  GEN_CHECK_USER: 'GEN_CHECK_USER',
  GEN_CHECK_DATE: 'GEN_CHECK_DATE',
  GEN_HAZARD: 'GEN_HAZARD',
  GEN_PHYSICAL_HAZARD: 'GEN_PHYSICAL_HAZARD',
  GEN_HEALTH_HAZARD: 'GEN_HEALTH_HAZARD',
  GEN_ENVIRONMENTAL_HAZARD: 'GEN_ENVIRONMENTAL_HAZARD',
  GEN_IS_CHECK_VALID: 'GEN_IS_CHECK_VALID',

  envAttrs: {
    ENV_TYPE_OF_PRESENCE: 'typePresence',
    ENV_WATERCOURSE: 'waterCourse',
    ENV_CONTAINER_INSPECTION: 'containerInspection',
    ENV_PIPING_INSPECTION: 'pipingInspection',
    ENV_PROCEDURE_LOAD_UNLOAD: 'procLoadUnload',
    ENV_ADEQUACY_OF_RETENTION: 'adequacyReten',
    ENV_TEST_OF_RETENTION_TIGHTNESS: 'testRetenTightness',
    ENV_INSPECTION_OF_LEAK_DETECTOR: 'leakDetectInspection',
    ENV_INSPECTION_OF_OVERFLOW_DETECTOR: 'overflowDetectInspection',
    ENV_ADEQUACY_OF_OIL_SEP_OR_BUFF_TANK: 'adequacySeparBufTank',
    ENV_INSPECTION_OF_OIL_SEP_OR_BUFF_TANK: 'separBufTankInspection',
    ENV_SPLIT_KIT: 'spillKit',
  },
};

export const isTrueOption = { text: 'Yes', key: 'Yes', value: 'true' };
export const isFalseOption = { text: 'No', key: 'No', value: 'false' };
export const isTrueFalseOptions = [isTrueOption, isFalseOption];

export const roles = {
  ROLE_VIW: 'ROLE_VIW',
  ROLE_EDT: 'ROLE_EDT',
  ROLE_CSE: 'ROLE_CSE',
  ROLE_ENV: 'ROLE_ENV',
  ROLE_FBE: 'ROLE_FBE',
  ROLE_MED: 'ROLE_MED',
  ROLE_TSO: 'ROLE_TSO',
  ROLE_DSO: 'ROLE_DSO',
  ROLE_VIW_GLOBAL: 'ROLE_VIW_GLOBAL',
  ROLE_PUB: 'ROLE_PUB',
  ROLE_CP_EDT: 'ROLE_CP_EDT',
  ROLE_HSE_DEV: 'ROLE_HSE_DEV',
  ROLE_ADMIN: 'ROLE_ADMIN',
  ROLE_FGSO: 'ROLE_FGSO',
  ROLE_CERES_API_MEDICAL_SERVICE: 'Medical API Service',
};

export const actionsKey = {
  ACTION_DESCRIPTION: 'ui.col.actionDescription',
  ACTION_RESOLVER: 'ui.col.actionResolver',
  ACTION_CREATEDTE: 'ui.col.actionCreatedte',
  ACTION_COMPLETIONDTE: 'ui.col.actionCompletiondte',
  ACTION_LINK: 'ui.col.actionlink',
  ACTION_COMPLETION_STATUS: 'ui.col.completionStatus',
  ACTION_DEADLINE_DATE: 'ui.col.deadlineDte',
};
